import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase";

// Types
interface TooltipPurchase {
  id: string;
  packageId: string;
  purchaserId: string;
  price: number;
  purchasedAt: Date | { seconds: number; nanoseconds: number } | string;
  createdAt?: Date | { seconds: number; nanoseconds: number } | string;
  [key: string]: unknown;
}

interface TooltipState {
  tooltipPurchases: TooltipPurchase[];
  totalTooltipSpent: number;
  tooltipSales: TooltipPurchase[];
  totalTooltipSales: number;
  totalAllTooltipPurchases: number;
  totalAllTooltipPurchasesCount: number;
  tooltipSatisfactionScore: number;
  tooltipRatingCounts: { [key: number]: number };
  loading: boolean;
  error: string | null;
  activeListeners: { [key: string]: () => void };
  lastFetchTimestamp: { [key: string]: number };
  allTooltipPurchases: TooltipPurchase[];
}

const initialState: TooltipState = {
  tooltipPurchases: [],
  totalTooltipSpent: 0,
  tooltipSales: [],
  totalTooltipSales: 0,
  totalAllTooltipPurchases: 0,
  totalAllTooltipPurchasesCount: 0,
  tooltipSatisfactionScore: 0,
  tooltipRatingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  loading: false,
  error: null,
  activeListeners: {},
  lastFetchTimestamp: {},
  allTooltipPurchases: [],
};

// Async thunk to fetch tooltip purchases with real-time updates
export const fetchTooltipPurchases = createAsyncThunk(
  "tooltip/fetchTooltipPurchases",
  async (userId: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as { tooltip: TooltipState };
      const cacheKey = `tooltipPurchases_${userId}`;
      const now = Date.now();
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes

      // Check if we have recent cached data
      if (
        state.tooltip.lastFetchTimestamp[cacheKey] &&
        now - state.tooltip.lastFetchTimestamp[cacheKey] < cacheExpiry
      ) {
        console.log("Using cached tooltip purchases data");
        return state.tooltip.tooltipPurchases;
      }

      // Set up real-time listener
      const purchasesRef = collection(db, "tooltipPurchases");
      const purchaseQuery = query(
        purchasesRef,
        where("purchaserId", "==", userId)
      );

      const unsubscribe = onSnapshot(
        purchaseQuery,
        (snapshot) => {
          if (!snapshot.metadata.fromCache) {
            console.log("Real-time update detected for tooltip purchases");

            const purchases: TooltipPurchase[] = [];
            let totalSpent = 0;

            snapshot.docs.forEach((doc) => {
              const data = doc.data();
              const purchase: TooltipPurchase = {
                id: doc.id,
                packageId: data.packageId || "",
                purchaserId: data.purchaserId || "",
                price: typeof data.price === "number" ? data.price : parseFloat(data.price) || 0,
                purchasedAt: data.purchasedAt,
                createdAt: data.createdAt,
                ...data,
              };
              purchases.push(purchase);
              totalSpent += purchase.price;
            });

            dispatch({
              type: "tooltip/updateTooltipPurchasesFromListener",
              payload: { purchases, totalSpent },
              meta: { cacheKey, timestamp: Date.now() },
            });
          }
        },
        (error) => {
          console.error("Error in tooltip purchases listener:", error);
          dispatch({
            type: "tooltip/setError",
            payload: "Failed to fetch tooltip purchases data",
          });
        }
      );

      // Store the listener for cleanup
      dispatch({
        type: "tooltip/setActiveListener",
        payload: { key: cacheKey, listener: unsubscribe },
      });

      // Initial fetch
      const snapshot = await getDocs(purchaseQuery);
      const purchases: TooltipPurchase[] = [];
      let totalSpent = 0;

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const purchase: TooltipPurchase = {
          id: doc.id,
          packageId: data.packageId || "",
          purchaserId: data.purchaserId || "",
          price: typeof data.price === "number" ? data.price : parseFloat(data.price) || 0,
          purchasedAt: data.purchasedAt,
          createdAt: data.createdAt,
          ...data,
        };
        purchases.push(purchase);
        totalSpent += purchase.price;
      });

      return {
        purchases,
        totalSpent,
        cacheKey,
        timestamp: Date.now(),
      };
    } catch (error: unknown) {
      console.error("Failed to fetch tooltip purchases:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch tooltip purchases";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to fetch tooltip sales (packages created by user and their purchases)
export const fetchTooltipSales = createAsyncThunk(
  "tooltip/fetchTooltipSales",
  async (creatorId: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as { tooltip: TooltipState };
      const cacheKey = `tooltipSales_${creatorId}`;
      const now = Date.now();
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes

      // Check if we have recent cached data
      if (
        state.tooltip.lastFetchTimestamp[cacheKey] &&
        now - state.tooltip.lastFetchTimestamp[cacheKey] < cacheExpiry
      ) {
        console.log("Using cached tooltip sales data");
        return state.tooltip.tooltipSales;
      }

      // First, fetch tooltip packages created by this user
      const packagesRef = collection(db, "tooltipPackages");
      const packagesQuery = query(
        packagesRef,
        where("creatorId", "==", creatorId)
      );

      const packagesSnapshot = await getDocs(packagesQuery);
      const packageIds: string[] = [];

      packagesSnapshot.docs.forEach((doc) => {
        packageIds.push(doc.id);
      });

      if (packageIds.length === 0) {
        return {
          sales: [],
          totalSales: 0,
          cacheKey,
          timestamp: Date.now(),
        };
      }

      // Then fetch all purchases for these packages
      const purchasesRef = collection(db, "tooltipPurchases");
      const allSales: TooltipPurchase[] = [];
      let totalSales = 0;

      // We need to query in batches since Firestore 'in' queries are limited to 10 items
      const batchSize = 10;
      for (let i = 0; i < packageIds.length; i += batchSize) {
        const batch = packageIds.slice(i, i + batchSize);
        const salesQuery = query(
          purchasesRef,
          where("packageId", "in", batch)
        );

        const salesSnapshot = await getDocs(salesQuery);

        salesSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const sale: TooltipPurchase = {
            id: doc.id,
            packageId: data.packageId || "",
            purchaserId: data.purchaserId || "",
            price: typeof data.price === "number" ? data.price : parseFloat(data.price) || 0,
            purchasedAt: data.purchasedAt,
            createdAt: data.createdAt,
            ...data,
          };
          allSales.push(sale);
          totalSales += sale.price;
        });
      }

      // Set up real-time listener for sales updates
      const unsubscribe = onSnapshot(
        packagesQuery,
        async (snapshot) => {
          if (!snapshot.metadata.fromCache) {
            console.log("Real-time update detected for tooltip packages");
            
            const updatedPackageIds: string[] = [];
            snapshot.docs.forEach((doc) => {
              updatedPackageIds.push(doc.id);
            });

            if (updatedPackageIds.length === 0) {
              dispatch({
                type: "tooltip/updateTooltipSalesFromListener",
                payload: { sales: [], totalSales: 0 },
                meta: { cacheKey, timestamp: Date.now() },
              });
              return;
            }

            // Fetch updated sales data
            const updatedSales: TooltipPurchase[] = [];
            let updatedTotalSales = 0;

            for (let i = 0; i < updatedPackageIds.length; i += batchSize) {
              const batch = updatedPackageIds.slice(i, i + batchSize);
              const salesQuery = query(
                purchasesRef,
                where("packageId", "in", batch)
              );

              const salesSnapshot = await getDocs(salesQuery);

              salesSnapshot.docs.forEach((doc) => {
                const data = doc.data();
                const sale: TooltipPurchase = {
                  id: doc.id,
                  packageId: data.packageId || "",
                  purchaserId: data.purchaserId || "",
                  price: typeof data.price === "number" ? data.price : parseFloat(data.price) || 0,
                  purchasedAt: data.purchasedAt,
                  createdAt: data.createdAt,
                  ...data,
                };
                updatedSales.push(sale);
                updatedTotalSales += sale.price;
              });
            }

            dispatch({
              type: "tooltip/updateTooltipSalesFromListener",
              payload: { sales: updatedSales, totalSales: updatedTotalSales },
              meta: { cacheKey, timestamp: Date.now() },
            });
          }
        },
        (error) => {
          console.error("Error in tooltip sales listener:", error);
          dispatch({
            type: "tooltip/setError",
            payload: "Failed to fetch tooltip sales data",
          });
        }
      );

      // Store the listener for cleanup
      dispatch({
        type: "tooltip/setActiveListener",
        payload: { key: cacheKey, listener: unsubscribe },
      });

      return {
        sales: allSales,
        totalSales,
        cacheKey,
        timestamp: Date.now(),
      };
    } catch (error: unknown) {
      console.error("Failed to fetch tooltip sales:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch tooltip sales";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to fetch all tooltip purchases (for admin dashboard)
export const fetchAllTooltipPurchases = createAsyncThunk(
  "tooltip/fetchAllTooltipPurchases",
  async (_, { dispatch }) => {
    const purchasesRef = collection(db, "tooltipPurchases");

    const unsubscribe = onSnapshot(
      purchasesRef,
      (snapshot) => {
        const purchases: TooltipPurchase[] = [];
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const purchase: TooltipPurchase = {
            id: doc.id,
            packageId: data.packageId || "",
            purchaserId: data.purchaserId || "",
            price: typeof data.price === "number" ? data.price : parseFloat(data.price) || 0,
            purchasedAt: data.purchasedAt,
            createdAt: data.createdAt,
            ...data,
          };
          purchases.push(purchase);
        });
        dispatch({
          type: "tooltip/updateAllTooltipPurchasesFromListener",
          payload: purchases,
        });
      },
      (error) => {
        console.error("Error in all tooltip purchases listener:", error);
        dispatch(setError("Failed to fetch all tooltip purchases data"));
      }
    );

    // Store the listener for cleanup
    dispatch(setActiveListener({ key: "allTooltipPurchases", listener: unsubscribe }));

    // Initial fetch
    const snapshot = await getDocs(purchasesRef);
    const purchases: TooltipPurchase[] = [];
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      const purchase: TooltipPurchase = {
        id: doc.id,
        packageId: data.packageId || "",
        purchaserId: data.purchaserId || "",
        price: typeof data.price === "number" ? data.price : parseFloat(data.price) || 0,
        purchasedAt: data.purchasedAt,
        createdAt: data.createdAt,
        ...data,
      };
      purchases.push(purchase);
    });

    return { purchases };
  }
);

// Cleanup function for listeners
export const cleanupTooltipListeners = () => (dispatch: (action: { type: string; payload: { key: string } }) => void, getState: () => { tooltip: TooltipState }) => {
  const state = getState();
  const activeListeners = state.tooltip.activeListeners;

  Object.keys(activeListeners).forEach((key) => {
    if (activeListeners[key]) {
      activeListeners[key]();
      dispatch({ type: "tooltip/cleanupListener", payload: { key } });
    }
  });
};

const tooltipSlice = createSlice({
  name: "tooltip",
  initialState,
  reducers: {
    updateTooltipPurchasesFromListener: (state, action) => {
      const { purchases, totalSpent } = action.payload;
      state.tooltipPurchases = purchases;
      state.totalTooltipSpent = totalSpent;
      state.loading = false;
      state.error = null;

      // Update cache timestamp
      if ('meta' in action && action.meta) {
        state.lastFetchTimestamp[(action.meta as { cacheKey: string }).cacheKey] = (action.meta as { timestamp: number }).timestamp;
      }
    },
    updateTooltipSalesFromListener: (state, action) => {
      const { sales, totalSales } = action.payload;
      state.tooltipSales = sales;
      state.totalTooltipSales = totalSales;
      state.loading = false;
      state.error = null;

      // Update cache timestamp
      if ('meta' in action && action.meta) {
        state.lastFetchTimestamp[(action.meta as { cacheKey: string }).cacheKey] = (action.meta as { timestamp: number }).timestamp;
      }
    },
    updateAllTooltipPurchasesFromListener: (state, action) => {
      state.allTooltipPurchases = action.payload;
    },
    setActiveListener: (state, action) => {
      const { key, listener } = action.payload;
      state.activeListeners[key] = listener;
    },
    cleanupListener: (state, action) => {
      const { key } = action.payload;
      if (state.activeListeners[key]) {
        state.activeListeners[key]();
        delete state.activeListeners[key];
      }
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearTooltipData: (state) => {
      state.tooltipPurchases = [];
      state.totalTooltipSpent = 0;
      state.tooltipSales = [];
      state.totalTooltipSales = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTooltipPurchases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTooltipPurchases.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === 'object' && 'purchases' in action.payload) {
          state.tooltipPurchases = action.payload.purchases;
          state.totalTooltipSpent = action.payload.totalSpent;
          state.lastFetchTimestamp[action.payload.cacheKey] = action.payload.timestamp;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchTooltipPurchases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTooltipSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTooltipSales.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === 'object' && 'sales' in action.payload) {
          state.tooltipSales = action.payload.sales;
          state.totalTooltipSales = action.payload.totalSales;
          state.lastFetchTimestamp[action.payload.cacheKey] = action.payload.timestamp;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchTooltipSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAllTooltipPurchases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTooltipPurchases.fulfilled, (state, action) => {
        state.allTooltipPurchases = action.payload.purchases;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAllTooltipPurchases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  updateTooltipPurchasesFromListener,
  updateTooltipSalesFromListener,
  updateAllTooltipPurchasesFromListener,
  setActiveListener,
  cleanupListener,
  setError,
  clearTooltipData,
} = tooltipSlice.actions;

export default tooltipSlice.reducer;
